import { ChangeEvent, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import { Entity } from '@backstage/catalog-model';

const NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

type Status =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken'; entity: Entity };

export const UniqueAppName = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, schema, required, rawErrors } = props;
  const catalogApi = useApi(catalogApiRef);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const value = formData ?? '';
  const patternValid = value === '' || NAME_PATTERN.test(value);

  useEffect(() => {
    if (!value || !patternValid) {
      setStatus({ kind: 'idle' });
      return undefined;
    }
    setStatus({ kind: 'checking' });
    const handle = setTimeout(async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: [
            { 'metadata.name': value },
            { 'metadata.name': `app-${value}` },
          ],
          fields: ['kind', 'metadata.name', 'metadata.namespace'],
        });
        if (items.length > 0) {
          setStatus({ kind: 'taken', entity: items[0] });
        } else {
          setStatus({ kind: 'available' });
        }
      } catch {
        setStatus({ kind: 'idle' });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [value, patternValid, catalogApi]);

  const helperText = (() => {
    if (!patternValid) {
      return 'Lowercase letters, digits, and hyphens only. Must start with a letter.';
    }
    if (rawErrors && rawErrors.length > 0) return rawErrors[0];
    switch (status.kind) {
      case 'checking':
        return 'Checking availability…';
      case 'available':
        return `✓ "${value}" is available.`;
      case 'taken':
        return `⚠ An entity named "${status.entity.metadata.name}" already exists (${status.entity.kind}). Pick a different name.`;
      default:
        return (
          (schema.description as string) ??
          'Used as the repo name (app-<name>) and the default subdomain.'
        );
    }
  })();

  const error = !patternValid || status.kind === 'taken';

  return (
    <FormControl fullWidth margin="normal" error={error}>
      <TextField
        label={schema.title ?? 'App Name'}
        value={value}
        required={required}
        error={error}
        helperText={helperText}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        placeholder="my-cool-app"
      />
    </FormControl>
  );
};
